import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { updateOwnDriverInfo } from '@/modules/driver/actions/async/updateOwnDriver';
import { getMyDriver } from '@/modules/driver/store/selectors';
import { AppDispatch } from '@/root-store';
import { useForm } from '@/shared/view/Form';
import { IDocsListItem } from '@src-common/view/components/DocsList';

import { EditOwnDriverInfoView } from './view';

interface IProps<T extends IDocsListItem> {
  docs: T[];
  onUploadDoc: (event: React.SyntheticEvent, document: T) => void;
  onFinishSubmit: () => void;
}

export const EditDriverInfo = <T extends IDocsListItem>({
  docs,
  onUploadDoc,
  onFinishSubmit,
}: IProps<T>) => {
  const dispatch = useDispatch() as AppDispatch;

  const driver = useSelector(getMyDriver);

  const postForm = useCallback(
    async (formValues: Parameters<typeof updateOwnDriverInfo>[0]) => {
      const { isSucceed, result = {} } = await dispatch(updateOwnDriverInfo(formValues)).unwrap();

      if (isSucceed && !result.validationErrors && !result.baseErrors) {
        onFinishSubmit();
      }

      return result;
    },
    [dispatch, onFinishSubmit],
  );

  const {
    handleSubmit,
    form: {
      control,
      formState: { isSubmitting },
    },
  } = useForm({
    onSubmit: postForm,
    defaultValues: {
      licenseNumber: driver.licenseNumber,
      licensingAuthority: driver.licensingAuthority,
      homeAddress: driver.homeAddress,
    },
  });

  return (
    <EditOwnDriverInfoView
      control={control}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit}
      docs={docs}
      onUploadDoc={onUploadDoc}
    />
  );
};
